import { styled, Typography } from '@mui/material';
import { useState, useCallback } from 'react';
import { ComponentButton } from '@/components/DynamicUI/formElements/buttons/ComponentButton';
import { Alert } from '@/components/DynamicUI/Alert';
import { ModalButton } from '@/components/DynamicUI/formElements/buttons/ModalButton';
import { ModalDialog } from '@/components/DynamicUI/ModalDialog';
import { AlertSeverity } from '@/constants/alertTypes';
import { useQuote, useQuoteStore } from '@/hooks/use-quote';
import { getValidationErrors, waitForPendingRequests, hasValidationErrors } from '@/hooks/use-quote';
import { reportError } from '@/services/error-reporting-service';
import { ButtonTypes, ButtonSizes, ButtonColors, ButtonVariants } from '@/constants/buttonTypes';
import useModal from '@/hooks/use-modals';
import { CoverageSubmitButton as CoverageSubmitButtonType } from '@/schemas/sceneDataSchema';
import useAnalytics, { eventName } from '@/hooks/use-analytics';

export const CoverageSubmitButton = ({ component }: { component: CoverageSubmitButtonType }) => {
  const quote = useQuote();
  const [isWaitingForPending, setIsWaitingForPending] = useState(false);
  const { getModal } = useModal();
  const { trackClick } = useAnalytics();

  const modalId = `${component.form_id}_validation_modal`;

  const handleSubmit = useCallback(async () => {
    if (!quote) return;

    // Check for immediate validation errors
    const errors = getValidationErrors(quote);
    if (errors.length > 0) {
      getModal(modalId)?.open();
      return;
    }

    setIsWaitingForPending(true);
    try {
      // Wait for any pending requests to complete
      await waitForPendingRequests();

      // Re-check validation after requests complete
      const currentQuote = useQuoteStore.getState().quote;
      if (currentQuote && hasValidationErrors(currentQuote)) {
        const recheckErrors = getValidationErrors(currentQuote);
        if (recheckErrors.length > 0) {
          getModal(modalId)?.open();
          return;
        }
      }

      const form = document.getElementById(component.form_id) as HTMLFormElement;
      form?.requestSubmit();
    } catch (error) {
      reportError({ error });
    } finally {
      setIsWaitingForPending(false);
    }
  }, [quote, component, modalId, getModal]);

  const validationErrors = quote ? getValidationErrors(quote) : [];

  return (
    <>
      <ComponentButton
        component={{
          id: component.form_id,
          form_id: component.form_id,
          label: component.label,
          size: component.size,
          color: ButtonColors.PRIMARY,
          start_icon: null,
          variant: ButtonVariants.CONTAINED,
          type: ButtonTypes.SUBMIT,
          end_icon: null,
          full_width: component.full_width,
        }}
        onClick={() => {
          trackClick(eventName(component.form_id), { button_type: component.type });
          void handleSubmit();
        }}
        type='button'
        loading={isWaitingForPending}
      />

      <ModalDialog
        id={modalId}
        status='closed'
        footerChildren={
          <ModalButton
            component={{
              id: `coverage_submit_button_close`,
              label: 'Got it',
              size: ButtonSizes.LARGE,
              color: ButtonColors.PRIMARY,
              start_icon: null,
              variant: ButtonVariants.CONTAINED,
              type: ButtonTypes.MODAL,
              modal_id: modalId,
              method_type: 'close',
              end_icon: null,
            }}
          />
        }
      >
        <Typography
          variant='h3'
          sx={{ mb: 1 }}
        >
          Please review your coverage selections
        </Typography>
        <Typography
          variant='body1'
          color='text.secondary'
          sx={{ mb: 2 }}
        >
          The following issues need to be resolved before you can continue.
        </Typography>
        <ErrorAlertsContainer>
          {validationErrors.map((error, index) => (
            <Alert
              key={index}
              severity={AlertSeverity.ERROR}
              title={error.name}
              titleGutterBottom={!!error.message}
              text={error.message || null}
            />
          ))}
        </ErrorAlertsContainer>
      </ModalDialog>
    </>
  );
};

const ErrorAlertsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
